import { cancel, select, takeEvery } from 'redux-saga/effects';
import {
  DruckRequestPayload,
  getGeschaeftsart,
  GlobalAction,
  GlobalActions,
  GlobalPut,
} from '@ps20-frontend/shop-integration-redux';
import {
  App,
  mapLeistungen,
  mapToBffVorvertragsdatenDruck,
  mapZusatzoptionenDruck,
  numberToGermanNumberFormattedString,
} from '@ps20-frontend/frontend-models';
import {
  Feature,
  SpartenType,
  VhvDruckdaten,
  VorschaedenDruck,
  ZusatzoptionenDruck,
} from '@ps20/bff-models';
import { PS20Logger } from '@ps20-frontend/logging';
import { getProduktlinienDruck, mapGefahren, mapLinienpreis } from '@ps20-frontend/druckdaten-lib';
import { safeSaga } from '@ps20-frontend/safe-saga';
import { selectFeatureToggle } from '@ps20-frontend/selector';
import { mapGesamtbeitragIfJaehrlichAndBeitragIsWrong } from '@ps20-frontend/preis-lib';
import {
  selectSelectedProduktlinie,
  selectZusatzoptionItems,
} from '../../app/shared/selectors/produkt.selector';
import { selectVorschaeden } from '../../app/shared/selectors/vorschaeden.selector';
import { Vorschaeden } from '../reducers/vorschaeden.reducer';
import { IAppState } from '../appState';
import { selectVertragsoptionen } from '../../app/shared/selectors/vertragsoption.selector';
import { VersicherungssummeEnum } from '../model/vertragsoptionen.model';

export function* watchDruckdatenChanged() {
  yield takeEvery(GlobalActions.DRUCKDATEN_REQUESTED, safeSaga(onDruckdatenRequested, App.VHV));
}

export function* onDruckdatenRequested(action: GlobalAction<DruckRequestPayload>) {
  try {
    const state: IAppState = yield select();

    const appIds: string[] = action.payload.appIds;

    if (!appIds.includes(App.VHV)) {
      yield cancel();
      return;
    }

    const vhvDruckdaten: VhvDruckdaten = mapDruckdaten(state);
    GlobalPut.getInstance().put(new GlobalActions().druckdatenGenerated(vhvDruckdaten));
  } catch (e) {
    PS20Logger.error(App.VHV, e as Error, 'DruckdatenSaga.onDruckdatenRequested()');
    yield cancel();
  }
}

function mapDruckdaten(state: IAppState): VhvDruckdaten {
  const vorschaeden: Vorschaeden = selectVorschaeden(state);
  const zusatzoptionenDruck: Array<ZusatzoptionenDruck> = mapZusatzoptionenDruck(
    selectZusatzoptionItems(state),
  );
  const ausgewaehlteProduktlinie = selectSelectedProduktlinie(state);
  const vertragsoptionen = selectVertragsoptionen(state);
  const vorteilsRabattActivated = selectFeatureToggle(state, Feature.VORTEILSRABATT);
  return {
    angebotsId: state.angebotsId,
    antragsnummer: state.antragsnummer,
    longText: ausgewaehlteProduktlinie.langtext,
    shortText: ausgewaehlteProduktlinie.kurztext,
    rbdAktuell: state.taasInfo.rbdNachlass,
    treuebonus: state.taasInfo.tbNachlass,
    vorteilsRabatt: vorteilsRabattActivated ? state.taasInfo.vorteilsrabatt || 0 : undefined,
    angebotsdatum: vorteilsRabattActivated ? state.vertragsoptionen.angebotsdatum : undefined,
    zaehlstuecke: state.rabattstufe,
    wohnung: {
      adresse: {
        plz: state.risikoort.plz,
        ort: state.risikoort.ort,
        strasse: state.risikoort.strasse,
        hausnummer: state.risikoort.hausnummer,
        latitude: state.risikoort.latitude,
        longtitude: state.risikoort.longtitude,
      },
      staendigbewohnt: state.versObjekt.staendigBewohnt,
      stockwerk: state.versObjekt.stockwerk,
      familienstand: state.versicherungsnehmer.familienstand,
      gebkategorie: state.versObjekt.hausart,
      wohnflaecheqm: state.versObjekt.wohnflaeche,
      auslandsAdresse: state.risikoort.auslandsadresse,
    },
    vertragsoptionen: {
      versicherungssumme: state.versicherungssummen
        .find(
          versicherungsumme =>
            versicherungsumme.produktlinie === ausgewaehlteProduktlinie.produktName,
        ).summe,
      wertsachensumme: state.wertsachensummen
        .find(
          wertsachensumme => wertsachensumme.produktline === ausgewaehlteProduktlinie.produktName,
        ).summe,
      individuelleVersicherungssumme:
        vertragsoptionen.versicherungssummeArt === VersicherungssummeEnum.INDV_VERSSUMME,
      unterversicherungsverzicht: state.unterversicherungsverzicht,
    },
    spartenType: SpartenType.HAUSRAT,
    beginn: null, // kommt vom Checkout
    ablauf: null, // kommt vom Checkout
    gesamtbeitrag: numberToGermanNumberFormattedString(
      mapGesamtbeitragIfJaehrlichAndBeitragIsWrong(
        state.vertragsoptionen.zahlungsweise,
        state.gesamtbeitragMitRabatt,
        state.jahresbeitrag,
        App.VHV,
      ),
    ),
    selbstbeteiligung: state.vertragsoptionen.selbstbeteiligung,
    vertragsdauer: null,
    linienpreis: mapLinienpreis(state.produkt),
    leistungen: mapLeistungen(
      ausgewaehlteProduktlinie,
      SpartenType.HAUSRAT,
      state.vertragsoptionen?.versicherungssumme,
    ),
    zusatzoptionenDruck,
    vorschaedenDruck: mapVorschaeden(vorschaeden),
    vorversicherungenDruck: null,
    gefahrenDruck: mapGefahren(state.produkt),
    produktlinien: getProduktlinienDruck(state.produkt),
    zuersGefaehrdungsklasse:
      state.gisDaten.zuersGefaehrdungsklasse != null
        ? state.gisDaten.zuersGefaehrdungsklasse.toString()
        : '',
    vorvertragsnummern: mapToBffVorvertragsdatenDruck(state.vertragsoptionen.vorvertragsNummern),
    vorvertragGekuendigt: state.vertragsoptionen.vorvertragGekuendigt,
    abweichungVomZielmarkt: false,
    beibehalten: state.vertragsoptionen.beibehalten,
    nachlass: ausgewaehlteProduktlinie.nachlass,
    auslandsRisiko: state.risikoort.auslandsrisiko,
    isGendernActive: selectFeatureToggle(state, Feature.GENDERN),
    kooperation: state.vertragsoptionen.selectedKooperation,
    geschaeftsart: getGeschaeftsart(state.vertragsoptionen),
  };
}

function mapVorschaeden(vorschaden: Vorschaeden): VorschaedenDruck {
  return {
    fahrrad: vorschaden.fahrrad,
    hausrat: vorschaden.fels,
    elementar: vorschaden.elementar,
  };
}
